import { ref } from 'vue'
import { defineStore } from 'pinia'

import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { ViewerDataGroupTwin, ViewerDataTwin } from 'models/ViewerDataTwin'
import {
  ALLOWED_VIEWER_DATA_GROUP_MODEL_IDS,
  ALLOWED_VIEWER_DATA_MODEL_IDS,
  VIEWER_DATA_MODEL_GROUP_REL_NAME
} from 'services/constants'
import { useDigitalTwinsStore } from './digital-twins'

function isViewerDataGroup(twin: BasicDigitalTwin): twin is ViewerDataGroupTwin {
  return ALLOWED_VIEWER_DATA_GROUP_MODEL_IDS.includes(twin.$metadata.$model)
}

/** The store owning the viewer-data catalog and its view state (visibility, opacity, selection, group expansion) */
export const useViewerDataStore = defineStore('viewerData', () => {
  /** Catalog groups of viewer-data twins */
  const groups = ref<ViewerDataGroupTwin[]>([])
  /** groupTwinId → resolved children (cached per group) */
  const groupChildren = ref<Record<string, ViewerDataTwin[]>>({})
  /** groupTwinId → whether its group children are loaded */
  const expanded = ref<Record<string, boolean>>({})
  /** groupTwinId → loading flag for lazy expansion */
  const loadingChildren = ref<Record<string, boolean>>({})
  /** twinId → user visibility */
  const visible = ref<Record<string, boolean>>({})
  /** twinId → transient opacity (0..1) */
  const opacity = ref<Record<string, number>>({})
  /** selected viewer-data twinId */
  const selectedTwinId = ref<string | undefined>(undefined)
  /** Viewer-data twins that are not part of any group */
  const ungrouped = ref<ViewerDataTwin[]>([])
  /** Whether the catalog is currently being loaded */
  const loading = ref(false)

  /** Load the viewer-data catalog: groups and the flat overview of viewer-data twins */
  async function loadCatalog() {
    if (loading.value) return
    loading.value = true
    try {
      const twinsStore = useDigitalTwinsStore()
      // NOTE: the group lookup intentionally uses an exact model-property filter (not
      // IS_OF_MODEL): this prd environment has no ViewerDataGroup twins and the exact
      // filter avoids the model-hierarchy expansion cost. The ViewerData catalog query
      // below MUST keep IS_OF_MODEL so derived viewer-data models (e.g. ForgeModel)
      // remain included — do not "align" it to the exact filter used here.
      const groupFilter = ALLOWED_VIEWER_DATA_GROUP_MODEL_IDS.map(
        (id) => `G.$metadata.$model = '${id}'`
      ).join(' OR ')
      const dataFilter = ALLOWED_VIEWER_DATA_MODEL_IDS.map((id) => `IS_OF_MODEL(T,'${id}')`).join(
        ' OR '
      )
      // Run the two lookups independently: a failing/timing-out group query must not
      // discard a successful ViewerData query (ungrouped data must still populate and
      // the panel must not stay stuck behind the spinner).
      const [groupsSettled, overviewSettled] = await Promise.allSettled([
        twinsStore.queryTwins('SELECT TOP(200) G FROM DIGITALTWINS G WHERE ' + groupFilter),
        twinsStore.queryTwins('SELECT TOP(200) T FROM DIGITALTWINS T WHERE ' + dataFilter)
      ])
      const groupTwins =
        groupsSettled.status === 'fulfilled'
          ? groupsSettled.value.twins.filter(isViewerDataGroup)
          : []
      groups.value = groupTwins
      // Eagerly resolve each group's children so group counts are correct immediately and
      // grouped twins can be excluded from `ungrouped`. Groups still start collapsed (we do not
      // touch `expanded`), and we do not set `loadingChildren` because the data is present
      // synchronously after this await. A failing children lookup resolves to [] so one bad
      // group cannot wipe out the rest of the catalog.
      const childrenSettled = await Promise.allSettled(
        groupTwins.map((group) =>
          twinsStore.getRelatedTwins(group.$dtId, VIEWER_DATA_MODEL_GROUP_REL_NAME)
        )
      )
      childrenSettled.forEach((result, index) => {
        groupChildren.value[groupTwins[index].$dtId] =
          result.status === 'fulfilled' ? result.value.twins : []
      })
      const groupedTwinIds = new Set<string>()
      Object.keys(groupChildren.value).forEach((groupId) => {
        groupChildren.value[groupId].forEach((t) => groupedTwinIds.add(t.$dtId))
      })
      const overviewTwins =
        overviewSettled.status === 'fulfilled' ? overviewSettled.value.twins : []
      ungrouped.value = overviewTwins.filter(
        (t): t is ViewerDataTwin => !groupedTwinIds.has(t.$dtId)
      )
    } finally {
      loading.value = false
    }
  }

  /** Lazily load a group's children (once) and toggle its expanded state */
  async function toggleGroupExpanded(groupId: string) {
    if (loadingChildren.value[groupId]) return
    if (!groupChildren.value[groupId]) {
      loadingChildren.value[groupId] = true
      try {
        const { twins } = await useDigitalTwinsStore().getRelatedTwins(
          groupId,
          VIEWER_DATA_MODEL_GROUP_REL_NAME
        )
        groupChildren.value[groupId] = twins
      } finally {
        loadingChildren.value[groupId] = false
      }
    }
    expanded.value[groupId] = !expanded.value[groupId]
  }

  /** Set the visibility of every known child of the provided group */
  function toggleGroupVisibility(groupId: string, visible: boolean) {
    groupChildren.value[groupId]?.forEach((twin) => {
      visibleTwin(twin.$dtId, visible)
    })
  }

  /** Set the visibility of a single viewer-data twin */
  function toggleVisibility(twin: ViewerDataTwin, visible: boolean) {
    visibleTwin(twin.$dtId, visible)
  }

  /** Store a transient opacity for a twin, clamped to the [0,1] range */
  function setOpacity(twinId: string, opacityValue: number) {
    if (Number.isFinite(opacityValue))
      opacity.value[twinId] = Math.min(1, Math.max(0, opacityValue))
  }

  /** Select a viewer-data twin */
  function select(twinId: string) {
    selectedTwinId.value = twinId
  }

  /** Reset all catalog and view state */
  function reset() {
    groups.value = []
    groupChildren.value = {}
    expanded.value = {}
    loadingChildren.value = {}
    visible.value = {}
    opacity.value = {}
    selectedTwinId.value = undefined
    ungrouped.value = []
    loading.value = false
  }

  /** Write helper so group/single visibility updates share one assignment path */
  function visibleTwin(twinId: string, value: boolean) {
    visible.value[twinId] = value
  }

  return {
    groups,
    groupChildren,
    expanded,
    loadingChildren,
    visible,
    opacity,
    selectedTwinId,
    ungrouped,
    loading,
    loadCatalog,
    toggleGroupExpanded,
    toggleGroupVisibility,
    toggleVisibility,
    setOpacity,
    select,
    reset
  }
})
